import { useMemo } from 'react'
import type { AppDispatch } from '@redux/store'

import { matchFilterArrayIncludes, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import * as publicationActions from '@redux/modules/publication'

import type { EntityOptions } from '@redux/modules/types'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { PublicationModel, PublicationRequestOptions } from '@domain-group/realhub-backend-api-types'

const watchEntityKeys = ['publications']

type LoadPublicationIdsParams = {
  dispatch: AppDispatch,
  publicationIds: number[],
  requestOptions?: EntityOptions,
}

const loadPublicationIds = (params: LoadPublicationIdsParams) => {
  const { dispatch, publicationIds, requestOptions } = params
  const { loadPublicationIds: loadFn } = publicationActions

  return dispatch(loadFn(publicationIds, requestOptions))
}

type PublicationFilters = {
  provider_ids?: number[],
  string?: string,
}

type UsePublicationsOptions = {
  filters?: PublicationFilters,
  performHttpRequests?: boolean,
  requestOptions?: PublicationRequestOptions,
}

function usePublications(options: UsePublicationsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    provider_ids: filterProviderIds,
    string: filterString,
  } = filters

  const {
    updatedEntities: { publications: publicationsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const dispatch = useDispatch()

  const { publications } = useSelector(reduxState => reduxState.entities)

  // Filter redux entities with provided filters
  const filteredPublications = useMemo(() => {
    const filtered = Object.values(publications).filter((publication: PublicationModel) => {
      const { id, provider_id, title } = publication

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchProviderIds = filterProviderIds ? matchFilterArrayIncludes(filterProviderIds, provider_id) : true
      return matchProviderIds && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [publicationsUpdatedAt, JSON.stringify(filters)])

  const filteredPublicationsCount = filteredPublications.length
  const hasFilteredPublications = !!filteredPublicationsCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredPublicationsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const reduxActionPayload = useReduxAction(
    'publications',
    'loadPublications',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  const { loading: loadingPublications, reduxActionPerformed } = reduxActionPayload

  return {
    callbacks: {
      loadMore,
      loadPublicationIds: (params: { publicationIds: number[], entityOptions?: EntityOptions }) => {
        const { publicationIds, entityOptions } = params
        return loadPublicationIds({ dispatch, publicationIds, requestOptions: entityOptions })
      },
    },
    canLoadMore,
    filteredPublications,
    filteredPublicationsCount,
    hasFilteredPublications,
    loading: loadingPublications,
    publications,
    reduxActionPerformed,
  }
}

export default usePublications
